import type { ReactNode } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faChevronRight,
  faMagnifyingGlass,
  faStore,
  faMoneyBillWave,
  faUserSlash,
  faArrowRight,
  faTriangleExclamation,
  faBoxesStacked,
  faTruckFast,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar } from './SiteChrome'
import { EbookHeaderCard } from './EbookPromo'
import { WhatsAppChannelSection } from './WhatsAppChannelSection'
import type { StorefrontConfig } from '../lib/dropify'
import type { FaqItem } from '../lib/seo'
import { whatsAppCtaProps, track } from '../lib/analytics'

const WA_NUMBER = '6285704011028'
const LEAD_SOURCE = 'reseller' as const

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

type StepItem = {
  icon: IconDefinition
  title: string
  body: string
  href?: string
  linkLabel?: string
  block?: 'payment'
}

export type PageContent = {
  analyticsId: string
  pageName: string
  breadcrumbLabel: string
  seoTitle: string
  seoDescription: string
  badge: string
  h1: string
  heroLead: ReactNode
  definitionTitle: string
  definitionBody: ReactNode
  stepsTitle: string
  steps: StepItem[]
  ctaMessage: string
  faq: FaqItem[]
}

const RESELLER_STEPS: StepItem[] = [
  {
    icon: faMagnifyingGlass,
    title: 'Pilih produk dari katalog',
    body: 'Buka katalog Dropify dan pilih produk yang akan kamu jual, misalnya kaos kaki, sarung tangan, handuk, atau pakaian dalam. Catat kode produk, varian, dan jumlah yang ingin diperiksa.',
    href: '/katalog',
    linkLabel: 'Lihat Katalog',
  },
  {
    icon: faBoxesStacked,
    title: 'Kulakan Stok Grosir',
    body: 'Kirim kode produk, varian, jumlah, dan kota tujuan kepada admin. MOQ serta satuan berbeda per kategori. Periksa quotation sebelum melakukan transfer.',
    href: '/minimum-order',
    linkLabel: 'Lihat Minimum Order tiap Kategori',
    block: 'payment',
  },
  {
    icon: faTruckFast,
    title: 'Pesanan dikirim ke alamatmu',
    body: 'Setelah pembayaran dikonfirmasi, admin akan memberi estimasi waktu penyiapan dan pengiriman sesuai isi pesanan serta kota tujuan. Untuk konten jualan, kamu dapat memakai foto di website atau meminta versi tanpa watermark kepada admin.',
  },
  {
    icon: faStore,
    title: 'Tentukan harga jual',
    body: 'Setelah stok kamu terima, tentukan harga jual dengan memasukkan harga beli, ongkir, kemasan, biaya platform, promosi, dan cadangan retur.',
    href: '/blog',
    linkLabel: 'Baca Tips Jualan',
  },
]

export const RESELLER_CONTENT: PageContent = {
  analyticsId: 'cara-jadi-reseller',
  pageName: 'Cara Jadi Reseller',
  breadcrumbLabel: 'Cara Jadi Reseller',
  seoTitle: 'Cara Jadi Reseller Grosir Tangan Pertama',
  seoDescription:
    'Cara menjadi reseller Dropify: pilih produk, cek MOQ, minta quotation melalui WhatsApp, simpan stok sendiri, lalu jual kembali dengan harga yang sudah memperhitungkan seluruh biaya.',
  badge: 'Beli dan simpan stok sendiri',
  h1: 'Cara Jadi Reseller',
  heroLead: (
    <>
      Sebagai <span className="font-semibold text-white">reseller</span> Dropify, kamu membeli produk sesuai MOQ,
      menyimpan stok sendiri, lalu menjualnya kembali. Pemilihan produk, quotation, dan konfirmasi pembayaran dilakukan
      melalui WhatsApp tanpa formulir pendaftaran.
    </>
  ),
  definitionTitle: 'Apa Itu Reseller?',
  definitionBody: (
    <>
      Reseller adalah penjual yang <span className="font-semibold text-slate-900">membeli produk dalam jumlah grosir
      lebih dulu</span> (kulakan), menyimpannya sebagai stok, lalu menjualnya kembali secara eceran atau grosir dengan
      harga yang kamu tentukan setelah menghitung seluruh biaya. Karena stok ada di tanganmu, pesanan pembeli dapat
      dikirim tanpa menunggu pembelian ulang. Di Dropify, MOQ dan satuan pembelian berbeda per kategori.
    </>
  ),
  stepsTitle: 'Empat tahap untuk mulai kulakan',
  steps: RESELLER_STEPS,
  ctaMessage: 'Halo Dropify, saya ingin menjadi reseller. Mohon kirim katalog, MOQ per kategori, dan cara meminta quotation.',
  faq: [
    {
      q: 'Apakah harus mendaftar dulu?',
      a: 'Tidak perlu. Dropify tidak memakai formulir atau biaya pendaftaran reseller. Kirim produk, jumlah, dan kota tujuan melalui WhatsApp untuk memulai proses order.',
    },
    {
      q: 'Berapa minimum kulakan untuk reseller?',
      a: 'MOQ berbeda per kategori. Handuk umumnya mulai 10 lusin dan kaos kaki umumnya 50 lusin. Buka halaman Minimum Order, lalu konfirmasikan produk serta jumlah kepada admin.',
    },
    {
      q: 'Apakah butuh modal untuk jadi reseller?',
      a: 'Ya. Reseller menyetok barang lebih dulu sesuai MOQ kategori. Hitung modal dari quotation, ongkir, kemasan, biaya platform, promosi, dan cadangan retur sebelum menentukan harga jual.',
    },
    {
      q: 'Bagaimana cara dapat foto produk tanpa watermark?',
      a: 'Kamu dapat memakai foto produk yang tampil di website atau meminta file tanpa watermark kepada admin. Pastikan foto, kode model, warna, dan ukuran sesuai dengan barang yang kamu miliki.',
    },
    {
      q: 'Semua transaksinya lewat mana?',
      a: 'Pengecekan stok, quotation, konfirmasi pesanan, dan konfirmasi pembayaran dilakukan melalui WhatsApp. Pembayaran dilakukan melalui transfer Mandiri atau BCA.',
    },
  ],
}

// Inline category link woven into the "Apa Itu Dropify" copy. Tracked like the
// other internal links so we can see which product mentions get clicked.
function CatLink({ slug, analyticsId, children }: { slug: string; analyticsId: string; children: ReactNode }) {
  return (
    <a
      href={`/kategori/${slug}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        track('click_internal_link', {
          link_url: `/kategori/${slug}`,
          link_text: slug,
          link_location: analyticsId,
        })
      }
      className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-2 transition-colors hover:decoration-blue-500"
    >
      {children}
    </a>
  )
}

function WhatsAppCta({
  message,
  label,
  pageName,
  className = '',
}: {
  message: string
  label: string
  pageName: string
  className?: string
}) {
  return (
    <a
      href={waLink(message)}
      {...whatsAppCtaProps(LEAD_SOURCE, { cta_placement: 'closing', lp_category: pageName })}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center gap-2 rounded-lg bg-green-700 px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white shadow-lg transition-colors hover:bg-green-800 ${className}`}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5" />
      {label}
    </a>
  )
}

function PaymentNote() {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3.5">
      <span className="text-base font-semibold text-slate-700">Menerima pembayaran transfer Mandiri &amp; BCA</span>
      <div className="flex items-center gap-4">
        <img src="/mandiri.png" alt="Bank Mandiri" className="h-6 w-auto" width={90} height={24} loading="lazy" />
        <img src="/bca.svg" alt="Bank BCA" className="h-6 w-auto" width={72} height={24} loading="lazy" />
      </div>
    </div>
  )
}

export function ResellerPage({
  config,
  content,
}: {
  config?: StorefrontConfig | null
  content: PageContent
}) {
  const { analyticsId, pageName, breadcrumbLabel } = content

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      {/* Header */}
      <div className="bg-slate-900 py-14">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <nav className="mb-4 flex items-center gap-1.5 text-xs text-slate-400">
                <a href="/" className="transition-colors hover:text-white">Beranda</a>
                <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
                <span className="font-medium text-white">{breadcrumbLabel}</span>
              </nav>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-green-500/15 px-3 py-1 text-xs font-semibold text-green-300 ring-1 ring-inset ring-green-500/30">
                <FontAwesomeIcon icon={faUserSlash} className="h-3.5 w-3.5" />
                {content.badge}
              </div>
              <h1 className="mb-3 text-4xl font-black uppercase tracking-tight text-white">{content.h1}</h1>
              <p className="max-w-2xl text-sm leading-relaxed text-slate-300">{content.heroLead}</p>
            </div>

            <EbookHeaderCard location={analyticsId} />
          </div>
        </div>
      </div>

      <WhatsAppChannelSection />

      <main className="mx-auto max-w-4xl px-4 py-12">
        {/* Definition of this role */}
        <section className="rounded-2xl border border-slate-100 bg-white p-8 md:p-10">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-blue-700">
            <FontAwesomeIcon icon={faCircleInfo} className="h-3.5 w-3.5" />
            Pengertian
          </div>
          <h2 className="mb-3 text-xl font-black uppercase tracking-tight text-slate-900">{content.definitionTitle}</h2>
          <p className="text-base leading-relaxed text-slate-600">{content.definitionBody}</p>
        </section>

        {/* Apa itu Dropify */}
        <section className="mt-8 rounded-2xl border border-slate-100 bg-white p-8 md:p-10">
          <h2 className="mb-3 text-xl font-black uppercase tracking-tight text-slate-900">Apa Itu Dropify?</h2>
          <p className="text-base leading-relaxed text-slate-600">
            Dropify adalah distributor grosir tangan pertama, bukan reseller. Kami menyuplai langsung ke pemilik toko dan
            penjual online di seluruh Indonesia melalui pemesanan WhatsApp. Produk yang kami jual antara
            lain <CatLink slug="kaos-kaki" analyticsId={analyticsId}>kaos kaki</CatLink>,{' '}
            <CatLink slug="manshet-sarung-tangan" analyticsId={analyticsId}>sarung tangan dan manshet</CatLink>, pakaian dalam seperti{' '}
            <CatLink slug="bra-lingerie" analyticsId={analyticsId}>bra</CatLink> serta{' '}
            <CatLink slug="cd-underwear" analyticsId={analyticsId}>CD dan underwear</CatLink>, sampai{' '}
            <CatLink slug="piyama-oneset" analyticsId={analyticsId}>piyama dan oneset</CatLink>. Semua produk
            dapat kamu pilih berdasarkan kode, varian, dan target pembeli toko.
          </p>
          <div className="mt-6 flex items-start gap-3 rounded-xl bg-amber-50 p-5 ring-1 ring-inset ring-amber-100">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
            <div>
              <p className="text-base font-bold text-slate-900">Dropify tidak berjualan secara eceran</p>
              <p className="mt-1 text-base leading-relaxed text-slate-600">
                Dropify tidak berjualan di marketplace maupun toko online mana pun yang menjual secara eceran. Kebijakan
                ini kami tetapkan agar tidak merugikan para pelanggan grosir kami dengan bersaing langsung di pasar
                eceran. Oleh karena itu, mohon berhati-hati terhadap pihak atau toko online yang mengatasnamakan Dropify.
                Seluruh transaksi resmi hanya dilayani melalui WhatsApp admin kami.
              </p>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                Dropify tidak menerima dropshipper. Kamu kulakan sesuai minimum order tiap kategori, stok barangnya
                sendiri, lalu kirim ke pembelimu.
              </p>
            </div>
          </div>
        </section>

        {/* Langkah-langkah */}
        <section className="mt-8">
          <h2 className="mb-1 text-xl font-black uppercase tracking-tight text-slate-900">{content.stepsTitle}</h2>
          <div className="mb-6 h-1 w-12 rounded bg-blue-500" />
          <div className="space-y-4">
            {content.steps.map((step, i) => (
              <div
                key={step.title}
                className="relative flex gap-4 overflow-hidden rounded-2xl border border-slate-100 bg-white p-6 sm:gap-6 sm:p-7"
              >
                {/* Left rail: icon + connector line */}
                <div className="flex flex-shrink-0 flex-col items-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
                    <FontAwesomeIcon icon={step.icon} className="h-5 w-5" />
                  </div>
                  {i < content.steps.length - 1 && <div className="mt-3 w-px flex-1 bg-slate-200" />}
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1 pb-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Langkah {i + 1}</span>
                  <h3 className="mb-2 mt-1 text-lg font-bold text-slate-900">{step.title}</h3>
                  <p className="text-base leading-relaxed text-slate-600">{step.body}</p>
                  {step.href && (
                    <a
                      href={step.href}
                      onClick={() =>
                        track('click_internal_link', {
                          link_url: step.href,
                          link_text: step.linkLabel,
                          link_location: analyticsId,
                        })
                      }
                      className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-4 py-2 text-sm font-bold text-blue-700 transition-colors hover:bg-blue-100"
                    >
                      {step.linkLabel}
                      <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
                    </a>
                  )}

                  {step.block === 'payment' && (
                    <>
                      <div className="mt-4 flex items-start gap-3 rounded-xl bg-green-50 p-4 ring-1 ring-inset ring-green-100">
                        <FontAwesomeIcon icon={faMoneyBillWave} className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                        <p className="text-base leading-relaxed text-slate-700">
                          Total pesanan dihitung admin berdasarkan produk, jumlah, dan ongkir. Setelah kamu{' '}
                          <span className="font-semibold text-slate-900">transfer sesuai quotation</span>, tim gudang
                          menyiapkan pesanan dan admin mengirim estimasi pengiriman.
                        </p>
                      </div>
                      <PaymentNote />
                    </>
                  )}
                </div>

                {/* Faint step number */}
                <div className="pointer-events-none absolute right-5 top-4 text-5xl font-black text-slate-100 sm:text-6xl">
                  {i + 1}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Semua transaksi via WhatsApp */}
        <section className="mt-8 overflow-hidden rounded-2xl bg-slate-900 p-8 text-center md:p-10">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-green-700 text-white">
            <FontAwesomeIcon icon={faWhatsapp} className="h-7 w-7" />
          </div>
          <h2 className="mb-2 text-xl font-black uppercase tracking-tight text-white">Semua Transaksi Lewat WhatsApp</h2>
          <p className="mx-auto mb-6 max-w-xl text-sm leading-relaxed text-slate-300">
            Pengecekan stok, quotation, konfirmasi pesanan, dan pembayaran dilakukan melalui WhatsApp. Kirim kategori
            yang ingin kamu jual agar admin dapat menjelaskan katalog, MOQ, dan cara order.
          </p>
          <WhatsAppCta message={content.ctaMessage} label="Kirim kebutuhan via WhatsApp" pageName={pageName} />
        </section>

        {/* FAQ */}
        <section className="mt-10">
          <h2 className="mb-1 text-xl font-black uppercase tracking-tight text-slate-900">Masih Ragu? Ini Jawabannya</h2>
          <div className="mb-6 h-1 w-12 rounded bg-blue-500" />
          <div className="space-y-3">
            {content.faq.map((item) => (
              <FaqRow key={item.q} item={item} faqLocation={analyticsId} />
            ))}
          </div>
          {analyticsId === 'cara-jadi-reseller' && (
            <p className="mt-6 text-sm leading-relaxed text-slate-600">
              Kalau ini kulakan pertamamu, baca <a href="/jaminan-ekspedisi" className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900">cara memeriksa kiriman lewat ekspedisi pilihan sebelum transfer</a>.
            </p>
          )}
        </section>
      </main>

      <Footer config={config} />
    </div>
  )
}

function FaqRow({ item, faqLocation }: { item: FaqItem; faqLocation: string }) {
  return (
    <details
      className="group rounded-xl border border-slate-100 bg-white p-5"
      onToggle={(e) => {
        if ((e.currentTarget as HTMLDetailsElement).open) {
          track('faq_open', { faq_question: item.q, faq_location: faqLocation })
        }
      }}
    >
      <summary className="flex cursor-pointer items-center justify-between gap-3 text-base font-bold text-slate-900 marker:content-none">
        {item.q}
        <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 flex-shrink-0 text-slate-400 transition-transform group-open:rotate-90" />
      </summary>
      <p className="mt-3 text-base leading-relaxed text-slate-600">{item.a}</p>
    </details>
  )
}
